import { useLocalization } from "@tm/localization"
import {
    Article,
    ArticleDetailsScrollTo,
    ArticleDetailsSubPage,
    ArticleReferenceParms,
    AvailabilityComponentProps,
    channel,
    DefaultArticleItemState,
    ErpInformation,
    SupplierArticleDto,
} from "@tm/models"
import { copyToClipboard } from "@tm/utils"
import { isEqual } from "lodash"
import { useCallback, useMemo, useState } from "react"
import { useDmsArticleInfo } from "@bundles/dms"
import { AvailabilityWrapper as AvailabilityComponent } from "@bundles/erp"
import { emptyFuncAsync, isAddButtonDisabled } from "@bundles/parts/components/ListV2/helpers"
import {
    useArticleActions,
    useArticleErpInfos,
    useArticleExtendedDeliveryInfo,
    useArticleImages,
    useArticleNotes,
    useArticleNotifications,
    useArticleSelection,
    useArticleTradeReferences,
    useDisplayArticle,
    useFavoriteArticleList,
    useGetRepairTimesUrl,
    useHandleAddToBasket,
    useHandleClickAvailability,
    useHandleClickDetails,
    useHandleClickImage,
    useHandleClickReference,
    useHandleRemoveFromBasket,
    useLoadErpInfos,
    useTraderErpInfos,
} from "@bundles/parts/components/ListV2/hooks"
import {
    useBasketQuantitiesContext,
    useErpInfosContext,
    useNotesContext,
    useProductGroupRepairTimesDataContext,
    useSupplierLogosContext,
    useTradeReferenceContext,
} from "@bundles/parts/components/ListV2/ContextProvider"
import { useArticleOptions } from "./useArticleOptions"
import { useCarouselActionsContext, useCarouselConfigurationContext } from ".."

export function useCarouselArticleItem(
    article: Article,
    generatedWorkTaskId: string,
    requestPartList: (showAddToBasketMessage?: boolean, articleOrder?: number) => void,
    articleOrder?: number
): DefaultArticleItemState {
    const { updateBasketQuantities, showArticleDetails } = useCarouselActionsContext()
    const { translateText } = useLocalization()

    const options = useArticleOptions(article)

    const [traderSelection, setTraderSelection] = useState(false)

    const toggleExpand = useCallback(() => {
        options.setExpandedArticleId?.(article.id)
    }, [article.id, options])

    const toggleTraderSelection = useCallback(() => {
        setTraderSelection((prev) => !prev)
    }, [])

    const erpInfosData = useErpInfosContext()
    const { basketQuantities } = useBasketQuantitiesContext()
    const [articleQuantity, setArticleQuantity] = useState(article.quantity || 1)
    const [originalQuantity, setOriginalQuantity] = useState(article.quantity || 1)
    const articleErpInfos = useArticleErpInfos(article, articleQuantity, erpInfosData)
    const defaultArticleErpInfoResponse = articleErpInfos.default?.state === "success" ? articleErpInfos.default.response : undefined
    const handleClickDetailsInternal = useHandleClickDetails(article, articleQuantity)
    const handleClickAvailability = useHandleClickAvailability(article, articleQuantity, erpInfosData)
    const { handleClickImage, handleCloseImage, showImageViewer } = useHandleClickImage(article)
    const handleClickReferenceInternal = useHandleClickReference(article, articleQuantity, showArticleDetails)
    const addToBasket = useHandleAddToBasket(article)
    const removeFromBasket = useHandleRemoveFromBasket()
    const articleImages = useArticleImages(article)
    const { tradeReferences: tradeRefs } = useTradeReferenceContext()
    const tradeReferences = useArticleTradeReferences(article, tradeRefs)
    const loadErpInfos = useLoadErpInfos(erpInfosData, tradeReferences)
    const traderErpInfos = useTraderErpInfos(article, erpInfosData, tradeReferences)
    const notifications = useArticleNotifications(articleErpInfos.default)
    const favoriteArticleList = useFavoriteArticleList(article)
    const selection = useArticleSelection()
    const {
        hideExtendedDeliveryComponent,
        feedbackConfiguration,
        enableArticleNotes,
        positionChangeEArtNrHArtNr,
        imsIcon,
        memoTooltipIcon,
        articleListTeccomMode,
    } = useCarouselConfigurationContext()

    const handleClickDetails = useCallback((subPage?: ArticleDetailsSubPage, scrollTo?: ArticleDetailsScrollTo) => {
        requestPartList()
        setTimeout(() => handleClickDetailsInternal(subPage, scrollTo), 1000)
    }, [])

    const handleClickReference = useCallback((reference: ArticleReferenceParms) => {
        requestPartList()
        setTimeout(() => handleClickReferenceInternal(reference), 1000)
    }, [])

    const extendedDeliveryInfo = useArticleExtendedDeliveryInfo(
        !hideExtendedDeliveryComponent,
        handleClickDetails,
        articleErpInfos.default,
        articleListTeccomMode
    )

    const basketQuantity = useMemo(
        () =>
            basketQuantities.find((q) =>
                isEqual(q.article, {
                    dataSupplierArticleNumber: article.supplierArticleNo,
                    dataSupplierId: article.supplier.id,
                    productGroupId: article.productGroup.id,
                } as SupplierArticleDto)
            ),
        [basketQuantities, article]
    )

    // Notes --------------------------------
    const notesContext = useNotesContext()
    const articleNotes = useArticleNotes(article, notesContext)
    // --------------------------------------

    // CostEstimation -----------------------
    const productGroupRepairTimes = useProductGroupRepairTimesDataContext()
    const repairTimeProviders = productGroupRepairTimes.productGroupRepairTimes?.productGroups[article.productGroup.id]

    const addButtonsDisabled = useMemo(() => isAddButtonDisabled(articleErpInfos, article.requestErpInfo), [articleErpInfos, article.requestErpInfo])
    // --------------------------------------

    const getRepairTimesUrl = useGetRepairTimesUrl()
    const repairTimesUrl = useMemo(() => {
        if (!!repairTimeProviders?.length && repairTimeProviders) {
            return getRepairTimesUrl(article, repairTimeProviders)
        }
    }, [repairTimeProviders, getRepairTimesUrl, article])

    const dmsArticleInfo = useDmsArticleInfo(article)

    const articleActions = useArticleActions({
        article,
        articleErpInfo: articleErpInfos.default,
        feedbackConfiguration,
        isNotesEnabled: enableArticleNotes,
        notes: articleNotes.notes,
        repairTimesUrl,
        onClickDetails: handleClickDetails,
        toggleExpand,
        toggleNotes: articleNotes.toggle,
        hideAlternatives: false,
        disableDeleteButton: true,
        options,
        generatedWorkTaskId,
        requestPartList,
    })

    // Supplier data ----------------------
    const { supplierLogos } = useSupplierLogosContext()
    const supplierLogo = useMemo(() => supplierLogos?.[article.supplier.id], [supplierLogos, article.supplier.id])
    // ------------------------------------

    const displayArticle = useDisplayArticle(article, [], supplierLogo, false)
    // ------------------------------------

    const postAddToBasket = useCallback(async () => {
        updateBasketQuantities?.([article])
    }, [article, updateBasketQuantities])

    const defaultArticleErpInfo = articleErpInfos.default
    const alternativArticleErpInfo = articleErpInfos.alternative

    const handleChangeQuantity = useCallback(
        (value: number, loadErpInfo = true) => {
            setArticleQuantity(value)
            setOriginalQuantity(value) // the only case where loadErp is false, is when division has changed the quantity and the catalog doesnt want another erprequest

            if (loadErpInfo) {
                if (defaultArticleErpInfo) {
                    loadErpInfos({ article, quantity: value, erpSystem: defaultArticleErpInfo.erpSystem })
                }
                if (alternativArticleErpInfo) {
                    loadErpInfos({ article, quantity: value, erpSystem: alternativArticleErpInfo.erpSystem })
                }
            }
        },
        [article, loadErpInfos, defaultArticleErpInfo, alternativArticleErpInfo]
    )

    const handleAddToBasket = useCallback(
        async (quantity?: number, erpInformation?: ErpInformation, erpSystemId?: number) => {
            if (!erpSystemId && tradeReferences.length > 1) {
                toggleTraderSelection()
                return
            }

            await addToBasket(
                quantity ?? articleQuantity,
                erpInformation ?? defaultArticleErpInfoResponse,
                erpSystemId ?? tradeReferences[0]?.erpSystemId,
                undefined,
                undefined,
                generatedWorkTaskId
            )
            postAddToBasket()
            handleChangeQuantity(article.quantity)
            requestPartList(true)
        },
        [
            tradeReferences,
            addToBasket,
            articleQuantity,
            defaultArticleErpInfoResponse,
            generatedWorkTaskId,
            postAddToBasket,
            handleChangeQuantity,
            article.quantity,
            requestPartList,
            toggleTraderSelection,
        ]
    )

    const handleRemoveFromBasket = useCallback(
        async (itemIds?: string[]) => {
            const ids = itemIds ?? basketQuantity?.articleQuantities?.allPartItemIds
            if (ids?.length) {
                await removeFromBasket(ids)
                updateBasketQuantities?.([article])
            }
        },
        [basketQuantity?.articleQuantities?.allPartItemIds, removeFromBasket, updateBasketQuantities, article]
    )

    const handleCopyArticleNumber = useCallback(
        (number: string) => {
            copyToClipboard(number)
            channel("APP").publish("TOAST_MESSAGE/SHOW", { message: translateText(1920) })
        },
        [translateText]
    )

    const AvailabilityComponentIntern = useCallback(
        (props: AvailabilityComponentProps) => (
            <AvailabilityComponent {...props} erpSystem={erpInfosData.erpConfig?.useOrderByDistributor ? props.erpSystem : undefined} />
        ),
        [erpInfosData.erpConfig?.useOrderByDistributor]
    )

    const showExtendedAvailabilityContainer = useMemo(
        () =>
            !!erpInfosData.erpConfig?.erpSystemConfigs &&
            erpInfosData.erpConfig?.erpSystemConfigs.filter((x) => x.useForGetErpInformation).length > 1,
        [erpInfosData.erpConfig?.erpSystemConfigs]
    )

    return useMemo(
        () => ({
            type: "default",
            isVehicleDependent: false,
            articleOrder,
            options,
            article,
            displayArticle,
            quantity: articleQuantity,
            originalQuantity,
            articleActions,
            articleErpInfos,
            basketQuantity,
            notes: articleNotes,
            tradeReferences,
            traderErpInfos,
            notifications,
            expanded: !!options.expanded,
            traderSelection,
            favoriteArticleList,
            showReplaceButton: false,
            positionChangeEArtNrHArtNr,
            selection,
            extendedDeliveryInfo,
            showImageViewer,
            articleImages,
            imsIcon,
            memoTooltipIcon,
            dmsArticleInfo,
            alreadyOrdered: false,
            showExtendedAvailabilityContainer,
            addToCostEstimationButtonDisabled: addButtonsDisabled,
            addToBasketButtonDisabled: addButtonsDisabled,
            showDescriptionCompactView: true,
            handleClickImage,
            handleCloseImage,
            handleClickDetails,
            handleChangeQuantity,
            handleClickReference,
            handleAddToBasket,
            handleAddToCostEstimation: emptyFuncAsync,
            handleRemoveFromBasket,
            handleCopyArticleNumber,
            handleClickAvailability,
            toggleExpand,
            toggleTraderSelection,
            AvailabilityComponent: AvailabilityComponentIntern,
        }),
        [
            articleOrder,
            options,
            article,
            displayArticle,
            articleQuantity,
            originalQuantity,
            articleActions,
            articleErpInfos,
            basketQuantity,
            articleNotes,
            tradeReferences,
            traderErpInfos,
            notifications,
            traderSelection,
            favoriteArticleList,
            positionChangeEArtNrHArtNr,
            selection,
            extendedDeliveryInfo,
            showImageViewer,
            articleImages,
            imsIcon,
            memoTooltipIcon,
            dmsArticleInfo,
            showExtendedAvailabilityContainer,
            addButtonsDisabled,
            handleClickImage,
            handleCloseImage,
            handleClickDetails,
            handleChangeQuantity,
            handleClickReference,
            handleAddToBasket,
            handleRemoveFromBasket,
            handleCopyArticleNumber,
            handleClickAvailability,
            toggleExpand,
            toggleTraderSelection,
            AvailabilityComponentIntern,
        ]
    )
}
